import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { createUrl, getPublicRoutesHeaders } from '../util/api.util';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { SecureService } from './secure.service';
import { IJWTPayload } from '../model/auth.model';
import { CurrentUser } from '../model/user.model';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { JwtService } from './jwt.service';
import { DataPayload } from '../model/api.model';
import { logger } from '../util/logger.util';
import { hasKey } from '../util/object.util';
import { Router } from '@angular/router';
import { forgotRequestData, registerRequestData, resetRequestData } from '../model/response.model';

const className = "UserAuthService";

@Injectable({
  providedIn: 'root'
})
export class UserAuthService {

  public currentUserSubject: BehaviorSubject<CurrentUser | null> = new BehaviorSubject<CurrentUser | null>(null);
  public currentUser: Observable<CurrentUser | null>;

  constructor(
    private __http: HttpClient,
    private __secureService: SecureService,
    private __jwtService: JwtService,
    private __router: Router
  ) {
    this.currentUser = this.currentUserSubject.asObservable();
  }

  /**
   * userRegister
   */
  public getCurrentUser(): Observable<DataPayload<CurrentUser>> {
    const signature = className + ".getCurrentUser: ";
    logger.silly(signature + 'Start');
    return this.__http.get<DataPayload<CurrentUser>>(createUrl('user_auth'))
      .pipe(
        tap(result => {
          logger.silly(signature, result);
          if (result.success) {
            this.currentUserSubject.next(result.data);
          } else {
            this.currentUserSubject.next(null);
          }
        }),
        catchError(err => {

          // "TODO: ONLY LOGOUT ON 403
          this.__jwtService.removeJWTData();

          throw (err);
        })
      );
  }

  /**
  * userRegisterAdmin
  */
  public getCurrentUserAdmin(): any {
    const signature = className + ".getCurrentUserAdmin: ";
    logger.silly(signature + 'Start');
    return this.__http.get<any>(createUrl('user_auth'))
      .pipe(
        map(result => {
          logger.silly(signature, result);
          if (result.success && result.data.role == 1) {
            this.getCurrentUser().subscribe();
            return true;
          } else {
            return false;
          }
        }));
  }

  /**
   * userRegisterUser
   */
  public getCurrentUserUser(): any {
    const signature = className + ".getCurrentUserUser: ";
    logger.silly(signature + 'Start');
    return this.__http.get<any>(createUrl('user_auth'))
      .pipe(
        map(result => {
          logger.silly(signature, result);
          if (result.success && result.data.role == 0) {
            this.getCurrentUser().subscribe();
            return true;
          } else {
            return false;
          }
        }));
  }

  /**
   * Clears all known JWT data and the current user
   */
  public logout() {
    this.__jwtService.removeJWTData();
    this.currentUserSubject.next(null);
    this.__router.navigate(['/user/login']);
  }

  /**
   * login
   */
  public login(credentials: {
    loginEmail: string,
    loginPassword: string
  }) {
    return this.__http.post<IJWTPayload | { loginChallenge: true }>(createUrl('user_auth', 'login'), credentials, {
      headers: getPublicRoutesHeaders()
    })
      .pipe(
        switchMap((payload) => {
          if (hasKey(payload, 'loginChallenge')) {
            return of(payload);
          }

          return this.__secureService.persistJwtPayload(payload as IJWTPayload)
        })
      );
  }

  public verify(credentials: {
    email: string,
    token: string
  }) {
    return this.__http.post<IJWTPayload>(createUrl('user_auth', 'verify'), credentials, {
      headers: getPublicRoutesHeaders()
    });
  }

  /**
   * @todo Move to UserGuardian service
   */
  public verifyGuardian(credentials: {
    email: string,
    token: string
  }) {
    return this.__http.post<IJWTPayload>(createUrl('user_guardian', 'verify'), credentials, {
      headers: getPublicRoutesHeaders()
    });
  }

  /**
   * @description Marks the application owned by the current user as completed
   * @returns
   */
  public markApplicationComplete() {
    return this.__http.get(createUrl('user_auth', 'complete'));
  }

  public register(credentials: Record<string, registerRequestData>) {
    return this.__http.post(createUrl('user_auth', 'register'), credentials, {
      headers: getPublicRoutesHeaders()
    });
  }

  public refreshToken(jwtString: string, jwtRefreshString: string) {
    return this.__http.post<IJWTPayload>(createUrl('user_auth', 'refresh'), {
      accessToken: jwtString,
      refreshToken: jwtRefreshString
    },
      { headers: getPublicRoutesHeaders() }
    );
  }

  public reset(credentials: resetRequestData) {
    return this.__http.post<IJWTPayload>(createUrl('user_auth', 'reset'), credentials, {
      headers: getPublicRoutesHeaders()
    })
      .pipe(
        switchMap((payload) => {
          return this.__secureService.persistJwtPayload(payload)
        })
      );
  }

  public forgot(credentials: Record<string, forgotRequestData>) {
    return this.__http.post(createUrl('user_auth', 'forgot'), credentials, {
      headers: getPublicRoutesHeaders()
    });
  }
}
